/**
 * Google Tag Manager
 * Loads the GTM container and exposes a typed helper for pushing events to the
 * dataLayer. The Google Ads conversion tag and Meta Pixel are configured inside
 * the GTM UI (container GTM-TNJ2K8J8) and fire off events pushed from here.
 * Documentation: https://developers.google.com/tag-platform/tag-manager/web
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * Initialize Google Tag Manager by injecting the container script into <head>.
 * Should be called once at app startup. Skips the <noscript> iframe — irrelevant
 * for a JS-required SPA.
 *
 * The dataLayer is created immediately so early events queue up, but the
 * script is injected only after the window `load` event: gtm.js plus the Meta
 * and Ads tags it pulls in (~270KB) would otherwise compete with the app's own
 * chunks on slow mobile. GTM replays the queued dataLayer when it loads.
 *
 * @param containerId - The GTM container ID (e.g. "GTM-XXXXXXX")
 */
export function initGtm(containerId: string): void {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": Date.now(),
      event: "gtm.js",
    });

    const injectScript = () => {
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
      document.head.appendChild(script);
    };
    if (document.readyState === "complete") {
      injectScript();
    } else {
      window.addEventListener("load", injectScript, { once: true });
    }
  } catch (error) {
    // Development logging for debugging GTM initialization
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error("Failed to initialize Google Tag Manager:", error);
    }
  }
}

/**
 * Push an event to the GTM dataLayer. No-op if GTM hasn't been initialized
 * (e.g. in development), so callers don't need to guard.
 *
 * @param event - The event object to push (must include an `event` key)
 */
export function pushToDataLayer(event: Record<string, unknown>): void {
  try {
    window.dataLayer?.push(event);
  } catch (error) {
    // Development logging for debugging dataLayer pushes
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error("Failed to push to GTM dataLayer:", error);
    }
  }
}
